import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

import { MITRA_DOCK_SELECTOR } from '@/features/mitra/store'

/**
 * The breathing room under the rail's last block, in rem: `--space-6`, 24px.
 * Kept above the window's bottom edge, or above the Mitra dock where the dock
 * sits under the rail's column.
 */
export const RAIL_BOTTOM_GAP_REM = 1.5

/** A box's edges, in viewport px — the part of a `DOMRect` this reads. */
export interface Edges {
  top: number
  left: number
  right: number
}

export interface BottomClearanceInput {
  /** `window.innerHeight`, in px. */
  viewportHeight: number
  /** The rail's own box. Only its left and right edges count. */
  rail: Edges
  /** The Mitra dock's box — the Ask Mitra bar and, over it while it has
   *  focus, the questions that rise — or `null` when there is no dock. */
  dock: Edges | null
  /** `RAIL_BOTTOM_GAP_REM`, in px. */
  gap: number
}

/**
 * How much of the window's bottom the rail must leave free, in px.
 *
 * The dock is `fixed` to the window's foot and spans the main column, which
 * on a screen with a rail stops a gutter short of the rail's own, so the two
 * normally do not meet. Wherever they do overlap side to side, the rail stops
 * `gap` above the dock's top — the bar's, or the questions' over it when they
 * show, since this is the dock's whole box. Measured, not declared: the
 * questions come and go with focus, the bar's width follows the column and
 * the sidebar, and the dock is not drawn at all below `lg` or with the panel
 * open. Where nothing overlaps, the rail keeps `gap` above the window's edge.
 */
export function computeBottomClearance({
  viewportHeight,
  rail,
  dock,
  gap,
}: BottomClearanceInput): number {
  const overlaps = dock !== null && dock.left < rail.right && dock.right > rail.left
  return overlaps ? Math.max(gap, viewportHeight - dock.top + gap) : gap
}

export interface RailAvailableHeightInput {
  /** The plain-pin position: `--header-h` + `--content-top`, in px. */
  headerOffset: number
  /** `window.innerHeight`, in px. */
  viewportHeight: number
  /** `computeBottomClearance`'s answer, in px. */
  bottomClearance: number
}

/**
 * How tall the rail may be: the gap between the pin and the bottom clearance.
 * What `computeRailFit` spends on blocks and rows. Nothing writes a
 * `max-height` from it — the rail's box is never capped or scrolled (a column
 * that scrolls inside a page that scrolls is two scrollbars arguing over one
 * gesture, and the owner rejected it on sight) — so it is a number the rail
 * chooses to live within.
 *
 * A function of the window, the header and the dock ALONE, never of the
 * rail's own height, which is why the loop it feeds settles: fewer rows make
 * the rail shorter, and a shorter rail must not move the budget that
 * shortened it.
 *
 * Floored at 0, so a window shorter than its own header hands the fit
 * "nothing fits", not a negative number to spend.
 */
export function computeRailAvailableHeight({
  headerOffset,
  viewportHeight,
  bottomClearance,
}: RailAvailableHeightInput): number {
  return Math.max(0, viewportHeight - headerOffset - bottomClearance)
}

export interface StickyTopInput {
  /** The plain-pin position: `--header-h` + `--content-top`, in px. */
  headerOffset: number
  /** The rail's own natural, unclipped height, in px. */
  railHeight: number
  /** `window.innerHeight`, in px. */
  viewportHeight: number
  /** `computeBottomClearance`'s answer, in px. */
  bottomClearance: number
}

/**
 * Where the sticky rail's `top` lands. The plain pin, `headerOffset`, for any
 * rail that fits — which `computeRailFit` makes every rail at any window 640px
 * tall or more, by trimming rows and then dropping blocks.
 *
 * What is left is a SAFETY NET, for the one case trimming cannot reach: a
 * window under 640px tall whose rail has nothing left to drop (a course's
 * call to action, Portfolio's three blocks). There this answers a smaller,
 * possibly negative `top`, so the rail travels with the page until its bottom
 * edge arrives `bottomClearance` above the window's, and pins there, rather
 * than pinning at the header with its last block below the fold for good.
 * Sticky paints at `max(flowPosition, top)`, so a negative inset hides
 * nothing at page-scroll 0.
 *
 * Why `top` and never a `top` + `bottom` pair: over-constrained sticky insets
 * resolve in favour of `top`, so the `bottom` would never engage.
 */
export function computeStickyTop({
  headerOffset,
  railHeight,
  viewportHeight,
  bottomClearance,
}: StickyTopInput): number {
  return Math.min(headerOffset, viewportHeight - railHeight - bottomClearance)
}

export interface StickyRailMetrics {
  /** The `top` (px) to pin the track at. */
  top: number | null
  /** The height (px) the rail should keep itself within: what
   *  `computeRailFit` spends, and nothing the browser enforces. */
  availableHeight: number | null
}

/** Shared so an unmeasured read is referentially stable across renders. */
const UNMEASURED: StickyRailMetrics = { top: null, availableHeight: null }

const pxOf = (value: string): number => Number(value.replace(/px$/, '')) || 0

/**
 * Measures the space the rail has and where to pin it, re-measuring on every
 * change that moves either: the rail's own height, the window's size, and the
 * dock's box (its questions rising with focus, or put away).
 *
 * Both numbers are `null` while unmeasured — jsdom, a `ResizeObserver`-less
 * environment, or the instant before the first layout pass — which is exactly
 * when the CSS pin on the track's own class list is what paints and the rail
 * draws whole.
 *
 * `enabled` is whether there is a track to pin; it is not a read of the `xl`
 * breakpoint. Below `xl` the track is `display: none` and an inline `top` is
 * inert, so there is nothing to gate twice.
 *
 * rAF-coalesced: a `ResizeObserver` firing mid-layout and a `resize` storm
 * collapse into at most one measurement per frame. It also measures again,
 * synchronously, after every render of the layout that owns it: the rail's
 * rows arrive in the same commit as its fit, so this sees the rail's full
 * height before the first paint rather than a frame after it.
 */
export function useStickyRailOffset(
  railRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): StickyRailMetrics {
  const [metrics, setMetrics] = useState<StickyRailMetrics>(UNMEASURED)
  const frameRef = useRef<number | null>(null)
  const measureRef = useRef<(() => void) | null>(null)

  useLayoutEffect(() => {
    if (!enabled) return

    const node = railRef.current
    if (node === null || typeof window === 'undefined' || typeof ResizeObserver === 'undefined') {
      return
    }

    const ro = new ResizeObserver(() => {
      schedule()
    })
    // The dock is the app shell's, not this page's: it can mount after the
    // rail (crossing `lg` on a resize), so every measurement looks it up
    // again and starts watching whichever box is there now.
    let watchedDock: Element | null = null

    const measure = () => {
      frameRef.current = null
      // The element's own resolved `top`, from the CSS class: the inline
      // `top` this hook last wrote is cleared for the read and put straight
      // back, or each answer would feed the next. Same task, so no frame
      // paints without it.
      const pinnedTop = node.style.top
      node.style.top = ''
      const headerOffset = pxOf(getComputedStyle(node).top)
      node.style.top = pinnedTop

      const dock = document.querySelector(MITRA_DOCK_SELECTOR)
      if (dock !== watchedDock) {
        if (watchedDock !== null) ro.unobserve(watchedDock)
        if (dock !== null) ro.observe(dock)
        watchedDock = dock
      }

      const viewportHeight = window.innerHeight
      const rail = node.getBoundingClientRect()
      const bottomClearance = computeBottomClearance({
        viewportHeight,
        rail,
        // The dock's box carries no transform of its own (its intro, hover
        // lift and suggestion's rise all move children), so this is where
        // it rests, not where an animation has it this frame.
        dock: dock === null ? null : dock.getBoundingClientRect(),
        gap: RAIL_BOTTOM_GAP_REM * pxOf(getComputedStyle(document.documentElement).fontSize),
      })
      const availableHeight = computeRailAvailableHeight({
        headerOffset,
        viewportHeight,
        bottomClearance,
      })
      const top = computeStickyTop({
        headerOffset,
        railHeight: rail.height,
        viewportHeight,
        bottomClearance,
      })

      setMetrics((previous) =>
        previous.top === top && previous.availableHeight === availableHeight
          ? previous
          : { top, availableHeight },
      )
    }

    const schedule = () => {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(measure)
    }

    // Synchronous first read, before the browser paints.
    measure()
    measureRef.current = measure

    ro.observe(node)
    window.addEventListener('resize', schedule)

    return () => {
      measureRef.current = null
      ro.disconnect()
      window.removeEventListener('resize', schedule)
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [railRef, enabled])

  // After every render, not only on a resize: see the note above. A read of
  // a few boxes, and `setMetrics` bails out when nothing moved.
  useLayoutEffect(() => {
    measureRef.current?.()
  })

  return enabled ? metrics : UNMEASURED
}
